import { useEffect, useRef } from "react";
import { tinykeys } from "tinykeys";

export interface ShortcutDef {
  /** tinykeys syntax: "$mod+k", "g i", "Shift+?" */
  keys: string;
  /** How the key is shown to people. */
  display: string[];
  label: string;
  group: "Navigation" | "Mail" | "Application";
}

// Single source for both the key bindings and the "?" cheatsheet, so the two cannot drift.
// Only shortcuts for features that exist in this build are listed.
export const SHORTCUTS = {
  palette: { keys: "$mod+k", display: ["Ctrl", "K"], label: "Command palette", group: "Application" },
  help: { keys: "Shift+?", display: ["?"], label: "Keyboard shortcuts", group: "Application" },
  search: { keys: "/", display: ["/"], label: "Search", group: "Application" },
  toggleSidebar: { keys: "[", display: ["["], label: "Collapse or expand sidebar", group: "Application" },
  goInbox: { keys: "g i", display: ["g", "i"], label: "Go to Inbox", group: "Navigation" },
  goDrafts: { keys: "g d", display: ["g", "d"], label: "Go to Drafts", group: "Navigation" },
  goSent: { keys: "g t", display: ["g", "t"], label: "Go to Sent", group: "Navigation" },
  goCalendar: { keys: "g c", display: ["g", "c"], label: "Go to Calendar", group: "Navigation" },
  goContacts: { keys: "g p", display: ["g", "p"], label: "Go to Contacts", group: "Navigation" },
  goSettings: { keys: "g ,", display: ["g", ","], label: "Go to Settings", group: "Navigation" },
  next: { keys: "j", display: ["j"], label: "Next conversation", group: "Mail" },
  previous: { keys: "k", display: ["k"], label: "Previous conversation", group: "Mail" },
  open: { keys: "Enter", display: ["Enter"], label: "Open conversation", group: "Mail" },
  back: { keys: "u", display: ["u"], label: "Back to list", group: "Mail" },
  select: { keys: "x", display: ["x"], label: "Select conversation", group: "Mail" },
  star: { keys: "s", display: ["s"], label: "Star or unstar", group: "Mail" },
  markRead: { keys: "Shift+I", display: ["Shift", "I"], label: "Mark as read", group: "Mail" },
  markUnread: { keys: "Shift+U", display: ["Shift", "U"], label: "Mark as unread", group: "Mail" },
  compose: { keys: "c", display: ["c"], label: "Compose", group: "Mail" },
  reply: { keys: "r", display: ["r"], label: "Reply (or your default)", group: "Mail" },
  replyAll: { keys: "a", display: ["a"], label: "Reply all", group: "Mail" },
  forward: { keys: "f", display: ["f"], label: "Forward", group: "Mail" },
  send: { keys: "$mod+Enter", display: ["Ctrl", "Enter"], label: "Send (in the composer)", group: "Mail" },
} as const satisfies Record<string, ShortcutDef>;

export type ShortcutId = keyof typeof SHORTCUTS;

/** True when a keystroke belongs to a text field and single-key shortcuts must not fire. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit", "reset", "range", "color"].includes(type);
  }
  return false;
}

const MODIFIED = new Set<ShortcutId>(["palette"]);

/** Binds shortcuts for the lifetime of the component. Single-key shortcuts are ignored while typing or when a dialog is open. */
export function useShortcuts(handlers: Partial<Record<ShortcutId, () => void>>, enabled = true): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });

  useEffect(() => {
    if (!enabled) return;
    const map: Record<string, (e: KeyboardEvent) => void> = {};
    for (const id of Object.keys(ref.current) as ShortcutId[]) {
      map[SHORTCUTS[id].keys] = (e) => {
        if (!MODIFIED.has(id)) {
          if (isTypingTarget(e.target)) return;
          if (document.querySelector("[role=dialog][data-state=open]")) return;
        }
        const h = ref.current[id];
        if (!h) return;
        e.preventDefault();
        h();
      };
    }
    return tinykeys(window, map);
    // Re-bind only when the set of handled ids changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, Object.keys(handlers).sort().join(",")]);
}
